import { QualifierObj } from "./_types";

/**
 * Recursively checks if a qualifier or any of its additionalQualifiers
 * has the target ID
 */
export function qualifierHasId(
  qualifier: QualifierObj,
  targetId: string
): boolean {
  if (qualifier.id === targetId) return true;
  if (
    qualifier.additionalQualifiers &&
    Array.isArray(qualifier.additionalQualifiers)
  ) {
    return qualifier.additionalQualifiers.some((q) =>
      qualifierHasId(q, targetId)
    );
  }
  return false;
}

/**
 * Checks if any ruleset in the array contains a qualifier with the target ID
 * (recursively checks additionalQualifiers)
 */
export function rulesetsContainQualifierId(
  rulesets: Array<{ qualifiers?: QualifierObj[] }>,
  targetId: string
): boolean {
  return rulesets.some((ruleset) => {
    if (!ruleset.qualifiers || !Array.isArray(ruleset.qualifiers)) return false;
    return ruleset.qualifiers.some((q) => qualifierHasId(q, targetId));
  });
}

/**
 * Checks if an IncentiveRules record contains a qualifier with the target ID.
 * Checks both legacy `rules` field and new `programs[].rules[]` structure.
 */
export function incentiveRulesContainsQualifierId(
  incentiveRules: {
    rules?: Array<{ qualifiers?: QualifierObj[] }> | null;
    programs?: Array<{ rules?: Array<{ qualifiers?: QualifierObj[] }> }> | null;
  },
  targetId: string
): boolean {
  // Check legacy rules field
  if (incentiveRules.rules && Array.isArray(incentiveRules.rules)) {
    if (rulesetsContainQualifierId(incentiveRules.rules, targetId)) {
      return true;
    }
  }

  // Check new programs field (programs[].rules[])
  if (incentiveRules.programs && Array.isArray(incentiveRules.programs)) {
    for (const program of incentiveRules.programs) {
      if (program.rules && Array.isArray(program.rules)) {
        if (rulesetsContainQualifierId(program.rules, targetId)) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Template data used when updating qualifiers
 */
export type QualifierTemplateData = {
  name: string;
  qualifier: QualifierObj;
};

/**
 * Recursively updates qualifiers with matching ID, including nested additionalQualifiers.
 * Returns a new array (does not mutate input).
 */
export function updateQualifiersWithTemplate(
  qualifiers: QualifierObj[],
  qualifierId: string,
  template: QualifierTemplateData
): { qualifiers: QualifierObj[]; updated: boolean } {
  let updated = false;

  const updatedQualifiers = qualifiers.map((q) => {
    // If this qualifier has the target ID, replace it (but preserve additionalQualifiers)
    if (q.id === qualifierId) {
      updated = true;
      // Recursively update additionalQualifiers if they exist
      let updatedAdditionalQualifiers = q.additionalQualifiers;
      if (q.additionalQualifiers && q.additionalQualifiers.length > 0) {
        const nestedResult = updateQualifiersWithTemplate(
          q.additionalQualifiers,
          qualifierId,
          template
        );
        if (nestedResult.updated) {
          updatedAdditionalQualifiers = nestedResult.qualifiers;
        }
      }

      return {
        ...template.qualifier,
        id: qualifierId,
        isTemplate: true,
        name: template.name,
        // Preserve existing additionalQualifiers (updated if needed)
        ...(updatedAdditionalQualifiers
          ? { additionalQualifiers: updatedAdditionalQualifiers }
          : {}),
      };
    }

    // If this qualifier doesn't match, still check its additionalQualifiers recursively
    if (q.additionalQualifiers && q.additionalQualifiers.length > 0) {
      const nestedResult = updateQualifiersWithTemplate(
        q.additionalQualifiers,
        qualifierId,
        template
      );
      if (nestedResult.updated) {
        updated = true;
        return {
          ...q,
          additionalQualifiers: nestedResult.qualifiers,
        };
      }
    }

    return q;
  });

  return { qualifiers: updatedQualifiers, updated };
}

/**
 * Updates all rulesets in an array, checking qualifiers recursively.
 * Mutates the rulesets array in place for performance.
 */
export function updateRulesetsWithTemplate(
  rulesets: Array<{ qualifiers?: QualifierObj[] }>,
  qualifierId: string,
  template: QualifierTemplateData
): { updated: boolean } {
  let updated = false;

  for (const ruleset of rulesets) {
    if (!ruleset.qualifiers || !Array.isArray(ruleset.qualifiers)) continue;

    const result = updateQualifiersWithTemplate(
      ruleset.qualifiers,
      qualifierId,
      template
    );

    if (result.updated) {
      ruleset.qualifiers = result.qualifiers;
      updated = true;
    }
  }

  return { updated };
}

/**
 * Updates an IncentiveRules record with a qualifier template.
 * Checks both legacy `rules` field and new `programs[].rules[]` structure.
 * Mutates the input object for performance.
 * Returns which fields were updated.
 */
export function updateIncentiveRulesWithTemplate(
  incentiveRules: {
    rules?: Array<{ qualifiers?: QualifierObj[] }> | null;
    programs?: Array<{ rules?: Array<{ qualifiers?: QualifierObj[] }> }> | null;
  },
  qualifierId: string,
  template: QualifierTemplateData
): { rulesUpdated: boolean; programsUpdated: boolean } {
  let rulesUpdated = false;
  let programsUpdated = false;

  // Check legacy rules field
  if (incentiveRules.rules && Array.isArray(incentiveRules.rules)) {
    const result = updateRulesetsWithTemplate(
      incentiveRules.rules,
      qualifierId,
      template
    );
    if (result.updated) {
      rulesUpdated = true;
    }
  }

  // Check new programs field (programs[].rules[])
  if (incentiveRules.programs && Array.isArray(incentiveRules.programs)) {
    for (const program of incentiveRules.programs) {
      if (!program.rules || !Array.isArray(program.rules)) continue;

      const result = updateRulesetsWithTemplate(
        program.rules,
        qualifierId,
        template
      );
      if (result.updated) {
        programsUpdated = true;
      }
    }
  }

  return { rulesUpdated, programsUpdated };
}
